import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import Spinner from "react-bootstrap/Spinner";
import { ArrowLeft, ExclamationTriangleFill, X } from "react-bootstrap-icons";
import { Card } from "../ui/RecordLayout";
import { HeaderActionButton } from "../ui/BowlerHeader";
import { api } from "../api/api";
import {
  specsheetBatchOpen,
  specsheetBatchClose,
  specsheetBatchUpload,
  specsheetBatchDiscard,
  specsheetBatchClear,
  specsheetCredits,
  SCAN_GUIDED_REASONS,
} from "./specsheet.redux";
import { ScanCreditsCount, ScanCreditsPanel } from "./ScanCredits";

/**
 * The inbox for a pile of scanned sheets, as a screen.
 *
 * A shop moving to Spectre arrives with a filing cabinet, not one sheet. The
 * server already reads four at a time and queues the rest, so the work here is
 * not speed -- it is keeping forty results legible while they land.
 *
 * It used to be a modal opened from the bowlers list, with the review as a
 * second modal on top of it. Nothing else in this application is a window
 * floating over another window, which is most of why the scan felt like a
 * different program bolted on. It is a screen now, at /scan, and a row opens
 * the review at its own address.
 *
 * Nothing is written here. A finished row hands its job to the review, which
 * is where values have always been confirmed.
 */

const Wrap = styled.div`
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
  padding: 16px;
  box-sizing: border-box;
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

const Back = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  /* Touch-first: tapped on a tablet at a shop counter. */
  width: 44px;
  height: 44px;
  padding: 0;
  border: none;
  background: transparent;
  color: #00187e;
  border-radius: 8px;

  &:hover,
  &:focus {
    background: #eef0f8;
  }
`;

const Titles = styled.div`
  min-width: 0;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #202124;
  line-height: 1.2;
`;

const Counts = styled.div`
  font-size: 12px;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Reading = styled.span`
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #00becc;
`;

const Actions = styled.div`
  margin-left: auto;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const Note = styled.div`
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 12px;
`;

const Problem = styled(Note)`
  color: #b02020;
`;

const Rows = styled(Card)`
  padding: 4px 8px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 4px;
  border-bottom: 1px solid #f0f1f5;

  &:last-child {
    border-bottom: none;
  }
`;

const Thumb = styled.div`
  flex: 0 0 auto;
  width: 34px;
  height: 44px;
  border-radius: 3px;
  border: 1px solid #e5e7eb;
  background: #f6f7fa center/cover no-repeat;
  background-image: ${(p) => (p.$src ? `url(${p.$src})` : "none")};
`;

const Names = styled.button`
  flex: 1 1 auto;
  min-width: 0;
  text-align: left;
  border: none;
  background: transparent;
  padding: 6px 4px;
  border-radius: 6px;
  color: inherit;

  &:disabled {
    cursor: default;
  }

  &:not(:disabled):hover {
    background: #f5f6fa;
  }
`;

const Name = styled.div`
  font-size: 13.5px;
  font-weight: 600;
  color: #202124;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FileName = styled.div`
  font-size: 10.5px;
  color: #9aa0ad;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Flags = styled.span`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 10.5px;
  color: #9a6100;
  background: #fef5e1;
  padding: 2px 9px;
  border-radius: 999px;
`;

const State = styled.span`
  flex: 0 0 auto;
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  padding: 3px 9px;
  border-radius: 999px;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$fg};
`;

/**
 * Always drawn, never revealed on hover: this list is worked through on a
 * tablet at a shop counter, where there is no hover to reveal it with.
 */
const Remove = styled.button`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  padding: 0;
  border: none;
  background: transparent;
  border-radius: 6px;
  color: #b9bec9;

  &:hover,
  &:focus {
    color: #d62828;
    background: #fdecec;
  }
`;

const Empty = styled.div`
  text-align: center;
  padding: 28px;
  color: #6b7280;
  font-size: 13.5px;
`;

const HiddenInput = styled.input`
  display: none;
`;

const readFile = (file) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      const comma = result.indexOf(",");
      resolve({
        name: file.name,
        mime: file.type || "application/octet-stream",
        base64: comma >= 0 ? result.slice(comma + 1) : result,
      });
    };
    // A file that will not read is dropped rather than failing the batch.
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(file);
  });

const STATES = {
  done: { bg: "#e8f5ee", fg: "#16803c" },
  running: { bg: "#e5f8fa", fg: "#00707a" },
  pending: { bg: "#f1f2f6", fg: "#6b7280" },
  failed: { bg: "#fdecec", fg: "#b02020" },
};

/**
 * Signed page URLs, built once per job and kept.
 *
 * The list is polled every few seconds while anything is still being read, so
 * signing every row's URL on each render would be forty pointless round trips
 * a minute for images the browser already has.
 */
const usePreviewUrls = (jobs) => {
  const [urls, setUrls] = useState({});
  const asked = useRef({});

  useEffect(() => {
    let alive = true;
    jobs.forEach((job) => {
      if (!job.hasPreview || asked.current[job.jobId]) return;
      asked.current[job.jobId] = true;
      api
        .getSessionApiUrl(`specSheet/scanPreview/${job.jobId}`, {})
        .then((url) => {
          if (alive && url) setUrls((c) => ({ ...c, [job.jobId]: url }));
        })
        // A row without its thumbnail is still a usable row.
        .catch(() => {});
    });
    return () => {
      alive = false;
    };
  }, [jobs]);

  return urls;
};

const ScanQueueScreen = ({
  jobs = [],
  uploading = 0,
  total = 0,
  error = "",
  errorReason = "",
  credits,
  applied = {},
  onOpen,
  onClose,
  onUpload,
  onDiscard,
  onClear,
  onGetCredits,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const previews = usePreviewUrls(jobs);
  // Emptying the list can throw away reads that were paid for and never
  // applied, so the button asks once. Two steps on the control itself rather
  // than a dialog on top of a screen.
  const [confirmingClear, setConfirmingClear] = useState(false);
  // The packs are shown on demand rather than sitting on the screen: a shop
  // with sheets left is here to work, not to be sold to.
  const [showPacks, setShowPacks] = useState(false);

  // Opening starts the poll; leaving stops it. Both are the existing actions:
  // the screen replaced the modal, not the machinery behind it.
  useEffect(() => {
    onOpen();
    return () => onClose();
  }, [onOpen, onClose]);

  // Asked once when the screen opens, and again whenever an upload is
  // refused -- that is exactly the moment the number on screen is stale.
  useEffect(() => {
    onGetCredits({});
  }, [onGetCredits]);

  // A refusal is answered by ScanLimitDialog, which is mounted for the whole
  // application -- so this screen no longer opens the packs itself. It used
  // to, and that was the right call while the dialog only existed on a
  // bowler's sheet; now it would mean two answers to one refusal, with the
  // inline one hidden behind the dialog. What is still this screen's job is
  // the number in its own header: it is stale the moment an upload is
  // refused.
  const refusedForCredits = errorReason === "no_credits";
  const refusedGuided = SCAN_GUIDED_REASONS.indexOf(errorReason) !== -1;
  useEffect(() => {
    if (refusedForCredits) onGetCredits({});
  }, [refusedForCredits, onGetCredits]);

  const handleFiles = async (event) => {
    const files = Array.from(event.target.files || []);
    // Let the same files be picked again after a batch finishes.
    event.target.value = "";
    if (!files.length) return;
    const read = (await Promise.all(files.map(readFile))).filter(Boolean);
    if (read.length) onUpload({ files: read });
  };

  const busy = total > 0;
  const reading = jobs.filter(
    (j) => j.status === "pending" || j.status === "running",
  ).length;
  const ready = jobs.filter(
    (j) => j.status === "done" && !applied[j.jobId],
  ).length;

  const titleFor = (job) => {
    if (job.status === "done") return job.name || t("specsheetScan.batch.noName");
    if (job.status === "failed") return job.message || t("specsheetScan.failed");
    return t(
      job.status === "running"
        ? "specsheetScan.batch.reading"
        : "specsheetScan.batch.waiting",
    );
  };

  const stateLabel = (job) => {
    if (job.status === "done") {
      return applied[job.jobId]
        ? t("specsheetScan.batch.applied")
        : t("specsheetScan.batch.ready");
    }
    if (job.status === "failed") return t("specsheetScan.batch.failedShort");
    return t(
      job.status === "running"
        ? "specsheetScan.batch.reading"
        : "specsheetScan.batch.waiting",
    );
  };

  return (
    <Wrap>
      <Head>
        <Back
          type="button"
          onClick={() => navigate("/bowlers")}
          aria-label={t("specsheetScan.batch.backToBowlers")}
          title={t("specsheetScan.batch.backToBowlers")}
        >
          <ArrowLeft size={20} />
        </Back>
        <Titles>
          <Title>{t("specsheetScan.batch.title")}</Title>
          <Counts>
            {reading > 0 && <Reading />}
            {t("specsheetScan.batch.counts", { ready, reading })}
            <ScanCreditsCount
              credits={credits}
              onBuy={() => setShowPacks((v) => !v)}
            />
          </Counts>
        </Titles>
        <Actions>
          <HiddenInput
            ref={inputRef}
            type="file"
            multiple
            accept=".jpg, .jpeg, .png, .pdf"
            onChange={handleFiles}
          />
          <HeaderActionButton
            disabled={busy}
            onClick={() => inputRef.current && inputRef.current.click()}
          >
            {t("specsheetScan.batch.pick")}
          </HeaderActionButton>
          {jobs.length > 0 && (
            <HeaderActionButton
              secondary
              disabled={busy}
              onBlur={() => setConfirmingClear(false)}
              onClick={() => {
                if (!confirmingClear) {
                  setConfirmingClear(true);
                  return;
                }
                setConfirmingClear(false);
                onClear();
              }}
            >
              {confirmingClear
                ? t("specsheetScan.batch.clearConfirm")
                : t("specsheetScan.batch.clear")}
            </HeaderActionButton>
          )}
        </Actions>
      </Head>

      <Note>{t("specsheetScan.batch.help")}</Note>
      {/* A refusal the dialog is already explaining must not also be
          restated here, or the operator reads the same sentence twice in two
          places. Everything else that went wrong belongs on the screen. */}
      {error && !refusedGuided ? <Problem>{error}</Problem> : null}

      {/* Asked for, now: browsing the packs with sheets still left. Being
          refused opens ScanLimitDialog instead. */}
      {showPacks && (
        <ScanCreditsPanel onClose={() => setShowPacks(false)} />
      )}

      {busy && (
        <Note>
          <Spinner animation="border" size="sm" />{" "}
          {t("specsheetScan.batch.uploading", { done: uploading, total })}
        </Note>
      )}

      <Rows>
        {jobs.length === 0 && !busy ? (
          <Empty>{t("specsheetScan.batch.empty")}</Empty>
        ) : (
          jobs.map((job) => {
            const openable = job.status === "done" && !applied[job.jobId];
            const tone = STATES[job.status] || STATES.pending;
            return (
              <Row key={job.jobId}>
                <Thumb $src={previews[job.jobId]} />
                <Names
                  type="button"
                  disabled={!openable}
                  onClick={() => navigate(`/scan/${job.jobId}`)}
                >
                  <Name>{titleFor(job)}</Name>
                  {job.label ? <FileName>{job.label}</FileName> : null}
                </Names>
                {job.needsReviewCount > 0 && !applied[job.jobId] && (
                  <Flags>
                    <ExclamationTriangleFill size={11} />
                    {job.needsReviewCount}
                  </Flags>
                )}
                <State $bg={tone.bg} $fg={tone.fg}>
                  {stateLabel(job)}
                </State>
                {/* Any row, whatever its state: a sheet picked by mistake is
                    worth removing before it is read, and one already worked
                    through is worth removing after. */}
                <Remove
                  type="button"
                  title={t("specsheetScan.batch.discard")}
                  aria-label={t("specsheetScan.batch.discard")}
                  onClick={() => onDiscard(job)}
                >
                  <X size={20} />
                </Remove>
              </Row>
            );
          })
        )}
      </Rows>
    </Wrap>
  );
};

const mapState = (state) => ({
  jobs: state.specsheet.batchJobs,
  uploading: state.specsheet.batchUploading,
  total: state.specsheet.batchTotal,
  error: state.specsheet.batchError,
  errorReason: state.specsheet.batchErrorReason,
  credits: state.specsheet.credits,
  applied: state.specsheet.batchApplied,
});

const mapDispatch = (dispatch) => ({
  onOpen: () => dispatch(specsheetBatchOpen({})),
  onClose: () => dispatch(specsheetBatchClose()),
  onUpload: (payload) => dispatch(specsheetBatchUpload(payload)),
  onDiscard: (job) => dispatch(specsheetBatchDiscard({ jobId: job.jobId })),
  onClear: () => dispatch(specsheetBatchClear({})),
  onGetCredits: (payload) => dispatch(specsheetCredits(payload)),
});

export const ScanQueueScreenContainer = connect(
  mapState,
  mapDispatch,
)(ScanQueueScreen);

export default ScanQueueScreenContainer;
